import html from "solid-js/html";
import FontAwesome from "fontawesome";

interface CustomIconDef {
    viewBox: [number, number];
    content: string;
}

interface IconProps {
    icon: string;
    size?: number | [number, number];
    class?: string;
    prefix?: string;
    // Per-icon style override: `true` forces solid (fas), `false` forces
    // regular (far); when omitted, follows the app-wide FA_DEFAULT_SOLID switch.
    // Solid falls back to regular when a solid variant isn't in the bundle.
    solid?: boolean;
}

// The FontAwesome family + default weight are theme-driven so this component
// stays identical across projects. Each project's CSS theme sets `--fa-style`
// (classic → far/fas, sharp → fasr/fass) and `--fa-default-solid` (0/1). Read
// once at load; falls back to classic/regular under SSR (no getComputedStyle).
function readIconTheme(): { regular: string; solid: string; defaultSolid: boolean } {
    let sharp = false, defaultSolid = false;
    if (typeof document !== "undefined" && typeof getComputedStyle === "function" && document.documentElement) {
        const cs = getComputedStyle(document.documentElement);
        sharp = cs.getPropertyValue("--fa-style").trim() === "sharp";
        defaultSolid = cs.getPropertyValue("--fa-default-solid").trim() === "1";
    }
    return { regular: sharp ? "fasr" : "far", solid: sharp ? "fass" : "fas", defaultSolid };
}

const _iconTheme = readIconTheme();
const FA_PREFIX_REGULAR = _iconTheme.regular;
const FA_PREFIX_SOLID = _iconTheme.solid;

// Set `--fa-default-solid: 1` in the theme to make the app default to solid icons.
// Individual icons still override per-call with `solid` (true/false) or `prefix`.
const FA_DEFAULT_SOLID: boolean = _iconTheme.defaultSolid;

const FA_PREFIX_DEFAULT = FA_DEFAULT_SOLID ? FA_PREFIX_SOLID : FA_PREFIX_REGULAR;

const ICON_BASE = "shrink-0";
const ICON_INLINE = "inline-block align-middle";

// Custom icon registry — overrides FontAwesome lookups.  Icons added
// here win over any FA name collision.
const customIcons: Record<string, CustomIconDef> = {};

// Register a custom icon that overrides FontAwesome for the given name.
// Prefer adding entries to the CUSTOM_ICONS block below so all custom
// SVGs live in one place; use this export only when a downstream file
// must register dynamically.
export function registerIcon(name: string, def: CustomIconDef) {
    customIcons[name] = def;
}

// --- Custom icon registrations ----------------------------------------------
// Keep all project-specific SVGs here so they're easy to find and audit.

customIcons["quickbooks"] = {
    viewBox: [512, 512],
    content: `
        <path fill-rule="evenodd" d="M256 16C123.5 16 16 123.5 16 256s107.5 240 240 240 240-107.5 240-240S388.5 16 256 16zm0 32c114.9 0 208 93.1 208 208s-93.1 208-208 208S48 370.9 48 256 141.1 48 256 48z"/>
        <path fill-rule="evenodd" d="M214 184c-31.5 0-57 25.5-57 57v30c0 31.5 25.5 57 57 57 12.7 0 24.4-4.2 33.9-11.2V362h26V184h-26v11.2c-9.5-7-21.2-11.2-33.9-11.2zm0 26c17.1 0 31 13.9 31 31v30c0 17.1-13.9 31-31 31s-31-13.9-31-31v-30c0-17.1 13.9-31 31-31z"/>
        <path fill-rule="evenodd" d="M265 150v178h26v-11.2c9.5 7 21.2 11.2 33.9 11.2 31.5 0 57-25.5 57-57v-30c0-31.5-25.5-57-57-57-12.7 0-24.4 4.2-33.9 11.2V150h-26zm59 60c17.1 0 31 13.9 31 31v30c0 17.1-13.9 31-31 31s-31-13.9-31-31v-30c0-17.1 13.9-31 31-31z"/>
    `,
};

customIcons["e-cd"] = {
    viewBox: [24, 16],
    content: `
        <svg xmlns="http://www.w3.org/2000/svg">
        <polygon points="12 0 0 16 24 16 12 0" fill="currentColor"/>
        <path d="M12.85,6.06c1.77,0,2.41,1.16,2.41,2.59,0,.65-.26,1.36-.38,1.57h-4.21c-.05,1.15.69,1.64,1.67,1.64.46,0,.99-.24,1.36-.48l.72,1.2c-.59.39-1.51.72-2.65.72-1.79,0-3.03-1.11-3.03-2.96,0-2.56,2.14-4.29,4.11-4.29ZM13.52,9.06c.04-.14.06-.32.06-.51,0-.56-.25-1.02-.99-1.02s-1.43.52-1.76,1.54h2.69Z" fill="#fff"/>
        </svg>
    `
}

interface FAIconDef {
    icon: [number, number, string[], string, string];
}

function resolveFAIcon(name: string, prefix: string): FAIconDef | undefined {
    return FontAwesome.findIconDefinition({ prefix, iconName: name });
}

export function Icon(props: IconProps) {
    const size = () => props.size ?? 16;

    const custom = () => customIcons[props.icon];
    // Style resolution: an explicit `prefix` wins; then a per-icon `solid`
    // override (true→solid, false→regular); otherwise the app-wide default set
    // by FA_DEFAULT_SOLID. The fallback chain lets any icon resolve regardless
    // of which style it actually ships in, so opting into solid never blanks.
    const faDef = () => {
        if (custom()) return undefined;
        let want: string;
        if (props.prefix) want = props.prefix;
        else if (props.solid === true) want = FA_PREFIX_SOLID;
        else if (props.solid === false) want = FA_PREFIX_REGULAR;
        else want = FA_PREFIX_DEFAULT;
        return resolveFAIcon(props.icon, want)
            || resolveFAIcon(props.icon, FA_PREFIX_REGULAR)
            || resolveFAIcon(props.icon, FA_PREFIX_SOLID);
    };
    const faData = () => faDef()?.icon;

    const vw = () => {
        const c = custom();
        if (c) return c.viewBox[0];
        const fa = faData();
        return fa ? fa[0] : 512;
    };
    const vh = () => {
        const c = custom();
        if (c) return c.viewBox[1];
        const fa = faData();
        return fa ? fa[1] : 512;
    };
    const svgContent = () => {
        const c = custom();
        if (c) return c.content;
        const fa = faData();
        return fa ? '<path d="' + fa[4] + '"/>' : "";
    };

    const h = (): number => {
        const s = size();
        return Array.isArray(s) ? (s[1] ?? s[0]) : s;
    };
    const w = (): number => {
        const s = size();
        return Array.isArray(s) ? s[0] : Math.round(h() * (vw() / vh()));
    };

    return (
        html`<svg xmlns="http://www.w3.org/2000/svg" viewBox=${() => "0 0 " + vw() + " " + vh()} fill="currentColor" width=${w} height=${h} class=${() => ICON_BASE + " " + (props.class || "")} innerHTML=${svgContent}></svg>`
    );
}

export function IconInline(props: IconProps) {
    return html`<${Icon} icon=${props.icon} size=${props.size} prefix=${props.prefix} solid=${props.solid} class=${() => ICON_INLINE + " " + (props.class || "")}/>`;
}

export function IconSuccess(props: IconProps) {
    return html`<${Icon} icon=${props.icon} size=${props.size} prefix=${props.prefix} solid=${props.solid} class=${() => ICON_INLINE + " text-green-600 " + (props.class || "")}/>`;
}

export function IconError(props: IconProps) {
    return html`<${Icon} icon=${props.icon} size=${props.size} prefix=${props.prefix} solid=${props.solid} class=${() => ICON_INLINE + " text-red-600 " + (props.class || "")}/>`;
}

interface IconContainerProps {
    children?: unknown;
}

export function IconContainer(props: IconContainerProps) {
    return html`<span class="flex flex-row items-center gap-2">${() => props.children}</span>`;
}
